import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import dayjs from "dayjs";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Cell } from "recharts";
import { Loader2, Search, ArrowUpDown, Copy, Mail, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { BudgetReport } from "@/components/NdisBudgetReport";

const fmtD = (v) => (v ? dayjs(v).format("D MMM YYYY") : "—");   // date-only business dates
const TONES = { good: "#059669", warn: "#d97706", bad: "#dc2626" };
const scoreTone = (s) => (s == null ? "text-slate-400" : s >= 90 ? "text-emerald-700" : s >= 60 ? "text-amber-700" : "text-rose-700");

function Stat({ label, value, tone }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4">
      <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">{label}</div>
      <div className={`text-2xl font-bold mt-1 ${tone || "text-slate-800"}`}>{value}</div>
    </div>
  );
}

function ChartCard({ title, children }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4">
      <div className="text-sm font-semibold text-slate-700 mb-2">{title}</div>
      <div className="h-56">{children}</div>
    </div>
  );
}

function Bars({ data, colors }) {
  return (
    <ResponsiveContainer width="100%" height="100%">
      <BarChart data={data} margin={{ left: -16, right: 8, top: 4 }}>
        <CartesianGrid strokeDasharray="3 3" vertical={false} />
        <XAxis dataKey="label" tick={{ fontSize: 11 }} interval={0} />
        <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
        <Tooltip />
        <Bar dataKey="count" radius={[4, 4, 0, 0]}>
          {data.map((d, i) => <Cell key={d.label} fill={colors[i % colors.length]} />)}
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  );
}

const SORTS = {
  name: (r) => r.name.toLowerCase(), phone: (r) => r.phone, email: (r) => r.email.toLowerCase(), lga: (r) => r.lga.toLowerCase(),
  ndis_number: (r) => r.ndis_number, poc: (r) => r.poc.join(", ").toLowerCase(),
  last_contact: (r) => r.last_contact || "", sa_end_date: (r) => r.sa_end_date || "9999",
  compliance: (r) => (r.compliance ?? -1),
};
const COLS = [
  ["name", "Participant"], ["phone", "Phone"], ["email", "Email"], ["lga", "LGA"], ["ndis_number", "NDIS number"],
  ["poc", "POC"], ["last_contact", "Last POC contact"], ["sa_end_date", "SA end date"], ["compliance", "Compliance"],
];

export default function NdisManagement() {
  const [inactive, setInactive] = useState(false);
  const [q, setQ] = useState("");
  const [sort, setSort] = useState({ k: "name", dir: 1 });
  const [picked, setPicked] = useState(new Set());
  const { data, isLoading, isError } = useQuery({
    queryKey: ["ndis-management", inactive],
    queryFn: () => api.ndisManagementOverview({ include_inactive: inactive }),
  });
  const rows = useMemo(() => data?.participants || [], [data]);
  const today = data?.today;

  const shown = useMemo(() => {
    const t = q.trim().toLowerCase();
    const f = t ? rows.filter((r) => [r.name, r.email, r.phone, r.lga, r.ndis_number, r.poc.join(" ")].join(" ").toLowerCase().includes(t)) : rows;
    const fn = SORTS[sort.k];
    return [...f].sort((a, b) => (fn(a) > fn(b) ? 1 : fn(a) < fn(b) ? -1 : 0) * sort.dir);
  }, [rows, q, sort]);

  const compBands = useMemo(() => {
    const b = [["Under 60%", 0], ["60–89%", 0], ["90–99%", 0], ["100%", 0]];
    rows.forEach((r) => { if (r.compliance != null) b[r.compliance >= 100 ? 3 : r.compliance >= 90 ? 2 : r.compliance >= 60 ? 1 : 0][1] += 1; });
    return b.map(([label, count]) => ({ label, count }));
  }, [rows]);

  const expiryBuckets = useMemo(() => {
    const b = { Expired: 0, "0–30 days": 0, "31–60 days": 0, "61–90 days": 0 };
    (data?.expiries || []).forEach((e) => {
      const days = dayjs(e.end_date).diff(dayjs(today), "day");
      if (e.expired) b.Expired += 1;
      else if (days <= 30) b["0–30 days"] += 1;
      else if (days <= 60) b["31–60 days"] += 1;
      else if (days <= 90) b["61–90 days"] += 1;
    });
    return Object.entries(b).map(([label, count]) => ({ label, count }));
  }, [data, today]);

  const upcoming = (data?.expiries || []).filter((e) => dayjs(e.end_date).diff(dayjs(today), "day") <= 90);
  const avg = rows.length ? Math.round(rows.reduce((s, r) => s + (r.compliance ?? 0), 0) / rows.length) : 0;
  const withEmail = shown.filter((r) => r.email);
  const toggleSort = (k) => setSort((s) => ({ k, dir: s.k === k ? -s.dir : 1 }));
  const chosen = withEmail.filter((r) => picked.has(r.id));
  const emailList = (chosen.length ? chosen : withEmail).map((r) => r.email);
  const copyEmails = async () => {
    try { await navigator.clipboard.writeText(emailList.join("; ")); toast.success(`Copied ${emailList.length} email address${emailList.length === 1 ? "" : "es"}`); }
    catch { toast.error("Couldn't copy. Select the addresses manually."); }
  };

  if (isLoading) return <div className="p-10 flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-slate-400" /></div>;
  if (isError) return <div className="p-10 text-sm text-rose-600" data-testid="ndis-mgmt-error">Couldn't load the NDIS Management data.</div>;

  return (
    <div className="p-4 sm:p-6 space-y-4" data-testid="ndis-management-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 tracking-tight font-brand">NDIS Management</h1>
          <p className="text-xs text-slate-500 mt-0.5">Participants at a glance, with compliance, expiry and contact reports.</p>
        </div>
        <label className="flex items-center gap-2 text-sm text-slate-600">
          <Switch checked={inactive} onCheckedChange={setInactive} data-testid="ndis-mgmt-inactive" />Include inactive
        </label>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Stat label="Participants" value={rows.length} />
        <Stat label="Average compliance" value={`${avg}%`} tone={scoreTone(avg)} />
        <Stat label="Fully compliant" value={rows.filter((r) => r.compliance === 100).length} tone="text-emerald-700" />
        <Stat label="Docs expired" value={rows.reduce((s, r) => s + r.docs_expired, 0)} tone="text-rose-700" />
      </div>

      <Tabs defaultValue="table">
        <TabsList>
          <TabsTrigger value="table">Participants</TabsTrigger>
          <TabsTrigger value="compliance">Compliance</TabsTrigger>
          <TabsTrigger value="expiry">Expiry dates</TabsTrigger>
          <TabsTrigger value="budget">Budget</TabsTrigger>
          <TabsTrigger value="contacts">Contact list</TabsTrigger>
        </TabsList>

        <TabsContent value="table" className="space-y-3">
          <div className="relative max-w-sm">
            <Search className="h-4 w-4 absolute left-2.5 top-2.5 text-slate-400" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name, LGA, NDIS, email, POC…" className="pl-8 h-9" data-testid="ndis-mgmt-search" />
          </div>
          <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white">
            <table className="w-full text-sm" data-testid="ndis-mgmt-table">
              <thead className="bg-slate-50 text-[11px] uppercase tracking-wide text-slate-500">
                <tr>{COLS.map(([k, l]) => (
                  <th key={k} className="text-left font-semibold px-3 py-2 whitespace-nowrap">
                    <button className="inline-flex items-center gap-1 hover:text-slate-800" onClick={() => toggleSort(k)}>{l}<ArrowUpDown className="h-3 w-3" /></button>
                  </th>))}</tr>
              </thead>
              <tbody>
                {shown.map((r) => (
                  <tr key={r.id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`ndis-mgmt-row-${r.id}`}>
                    <td className="px-3 py-2 font-medium text-slate-800 whitespace-nowrap">{r.name}</td>
                    <td className="px-3 py-2 whitespace-nowrap">{r.phone || "—"}</td>
                    <td className="px-3 py-2">{r.email || "—"}</td>
                    <td className="px-3 py-2 whitespace-nowrap">{r.lga || "—"}</td>
                    <td className="px-3 py-2 whitespace-nowrap">{r.ndis_number || "—"}</td>
                    <td className="px-3 py-2">{r.poc.join(", ") || "—"}</td>
                    <td className="px-3 py-2 whitespace-nowrap">{fmtD(r.last_contact)}</td>
                    <td className="px-3 py-2 whitespace-nowrap">{fmtD(r.sa_end_date)}</td>
                    <td className={`px-3 py-2 font-semibold ${scoreTone(r.compliance)}`}>{r.compliance == null ? "—" : `${r.compliance}%`}</td>
                  </tr>))}
                {!shown.length && <tr><td colSpan={COLS.length} className="px-3 py-8 text-center text-slate-400">No participants match.</td></tr>}
              </tbody>
            </table>
          </div>
        </TabsContent>

        <TabsContent value="compliance" className="grid gap-4 lg:grid-cols-2">
          <ChartCard title="Participants by compliance score"><Bars data={compBands} colors={[TONES.bad, TONES.warn, "#65a30d", TONES.good]} /></ChartCard>
          <div className="rounded-xl border border-slate-200 bg-white p-4">
            <div className="text-sm font-semibold text-slate-700 mb-2 flex items-center gap-1.5"><AlertTriangle className="h-4 w-4 text-amber-500" />Lowest scores</div>
            <ul className="divide-y divide-slate-100 text-sm">
              {[...rows].filter((r) => r.compliance != null && r.compliance < 100).sort((a, b) => a.compliance - b.compliance).slice(0, 10).map((r) => (
                <li key={r.id} className="py-1.5 flex justify-between gap-2">
                  <span className="truncate">{r.name}</span>
                  <span className="text-slate-500 whitespace-nowrap">{r.docs_missing} missing · {r.docs_expired} expired · <b className={scoreTone(r.compliance)}>{r.compliance}%</b></span>
                </li>))}
            </ul>
          </div>
        </TabsContent>

        <TabsContent value="expiry" className="grid gap-4 lg:grid-cols-2">
          <ChartCard title="Compliance documents by time to expiry"><Bars data={expiryBuckets} colors={[TONES.bad, TONES.warn, "#eab308", "#65a30d"]} /></ChartCard>
          <div className="rounded-xl border border-slate-200 bg-white p-4 max-h-[26rem] overflow-y-auto">
            <div className="text-sm font-semibold text-slate-700 mb-2">Expired or expiring within 90 days ({upcoming.length})</div>
            <table className="w-full text-sm"><tbody>
              {upcoming.map((e, i) => (
                <tr key={`${e.participant}-${e.document}-${i}`} className="border-t border-slate-100">
                  <td className="py-1.5 pr-2">{e.participant_name}</td><td className="pr-2 text-slate-500">{e.document}</td>
                  <td className="whitespace-nowrap">{fmtD(e.end_date)}</td>
                  <td className={`pl-2 font-semibold text-[11px] ${e.expired ? "text-rose-600" : "text-amber-600"}`}>{e.expired ? "EXPIRED" : "EXPIRING"}</td>
                </tr>))}
              {!upcoming.length && <tr><td className="py-6 text-center text-slate-400">Nothing expiring soon.</td></tr>}
            </tbody></table>
          </div>
        </TabsContent>

        <TabsContent value="budget" className="space-y-4">
          <BudgetReport includeInactive={inactive} search={q} />
        </TabsContent>

        <TabsContent value="contacts" className="space-y-3">
          <div className="flex flex-wrap items-center gap-2">
            <Button size="sm" variant="outline" onClick={copyEmails} disabled={!emailList.length} data-testid="ndis-mgmt-copy-emails">
              <Copy className="h-3.5 w-3.5 mr-1.5" />Copy {chosen.length ? "selected" : "all"} emails ({emailList.length})
            </Button>
            <Button size="sm" asChild><Link to="/bulk-email"><Mail className="h-3.5 w-3.5 mr-1.5" />Open Bulk Email</Link></Button>
            <span className="text-xs text-slate-400">Uses the search on the Participants tab. Tick rows to copy only those.</span>
          </div>
          <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white">
            <table className="w-full text-sm"><thead className="bg-slate-50 text-[11px] uppercase tracking-wide text-slate-500"><tr>
              <th className="w-8 px-3 py-2"><Checkbox checked={withEmail.length > 0 && chosen.length === withEmail.length} onCheckedChange={(c) => setPicked(c ? new Set(withEmail.map((r) => r.id)) : new Set())} /></th>
              {["Participant", "Email", "Phone"].map((l) => <th key={l} className="text-left font-semibold px-3 py-2">{l}</th>)}</tr></thead>
              <tbody>{shown.map((r) => (
                <tr key={r.id} className="border-t border-slate-100">
                  <td className="px-3 py-2"><Checkbox disabled={!r.email} checked={picked.has(r.id)} onCheckedChange={(c) => setPicked((s) => { const n = new Set(s); c ? n.add(r.id) : n.delete(r.id); return n; })} /></td>
                  <td className="px-3 py-2 font-medium">{r.name}</td>
                  <td className="px-3 py-2">{r.email || <span className="text-amber-600 text-xs">No email on file</span>}</td>
                  <td className="px-3 py-2">{r.phone || "—"}</td></tr>))}</tbody></table>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
